import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type ExpanderComponentProps, type TableColumn } from "react-data-table-component";
import { TablePager } from "../shared/TablePager";
import { adminTableTheme } from "../admin/adminTableTheme";
import styles from "../admin/Admin.module.css";
import detailStyles from "./RequestHistoryTable.module.css";
import type { PersonRequestHistoryItem, ProviderChange } from "../../api/requestsAdmin";
import { isProviderAllocationRequest } from "../../api/requestTypes";
import { ProviderName } from "../shared/ProviderName";
import { RequestTimeline } from "./RequestTimeline";
import { LoadingState } from "../states";
import { CancelRequestButton } from "./CancelRequestButton";
import { TeamName } from "./TeamName";
import { WaitingWith } from "./WaitingWith";
import { formatDate, isChanged, shortEmail, statusLabel, typeLabel } from "./requestHistoryFormat";

const ManualVendorAction = "ManualVendorAction";
const JoinGroupRequest = "JoinGroup";

/** The fields the shared request details need from any request. */
interface RequestDetailContentProps {
  requestType: string;
  payloadJson: string;
  providerChanges: ProviderChange[] | null;
  /** Shows the details to the person the request is about rather than to an administrator. */
  forRequester?: boolean;
  /** The request's status; with `forRequester`, it decides how a manual vendor task is explained. */
  status?: string;
}

interface ManualVendorTask {
  vendorId: string;
  action: string;
  instructions: string;
  reason: string;
}

/**
 * A manual vendor task's action, with a coloured mark for the two the portal raises: a green plus for granting access and
 * a red minus for removing it. The words always say which it is; the mark only makes it quicker to see.
 */
function ActionName({ action }: { action: string }): React.JSX.Element {
  const kind = action === "Grant access" ? "grant" : action === "Remove access" ? "remove" : null;
  if (kind === null) {
    return <>{action}</>;
  }

  return (
    <span className={detailStyles.action}>
      <svg className={kind === "grant" ? detailStyles.grant : detailStyles.remove} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" />
        <path d={kind === "grant" ? "M12 7.5v9M7.5 12h9" : "M7.5 12h9"} />
      </svg>
      {action}
    </span>
  );
}

/** Parses a request payload into a plain object, or null when it is not valid JSON. */
function parsePayload(payloadJson: string): Record<string, unknown> | null {
  try {
    const payload: unknown = JSON.parse(payloadJson);
    return typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Reads a string field under either its camel-case or Pascal-case name, as the server may write either. */
function field(payload: Record<string, unknown>, name: string): string {
  const value = payload[name] ?? payload[name.charAt(0).toUpperCase() + name.slice(1)];
  return typeof value === "string" ? value : "";
}

/** The vendor, action, reason and instructions of a manual vendor task. */
function manualVendorTask(payload: Record<string, unknown>): ManualVendorTask {
  return { vendorId: field(payload, "vendorId"), action: field(payload, "action"), instructions: field(payload, "instructions"), reason: field(payload, "reason") };
}

const wholeDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/**
 * A provider-allocation request as a grid: one column per provider it lists, with a row for the setting before the
 * request and a row for the setting it asks for. A cell shows only the amount, or a dash when the provider is not
 * enabled. A cell whose value changes is bold. A provider switched off shows $0 struck through in the After row; a
 * provider that stays on at $0 shows $0 without a strikethrough.
 *
 * @param props.changes - The providers the request lists, each with its before and after setting.
 */
function ProviderChangeTable({ changes }: { changes: ProviderChange[] }): React.JSX.Element {
  const { t } = useTranslation();
  const providerName = (vendorId: string) => t(`admin.entitlement.person.allocation.vendors.${vendorId}`, vendorId);
  const notEnabled = <span aria-hidden="true">—</span>;
  const notEnabledForScreenReaders = t("requests.history.details.notEnabled", "Not enabled");

  return (
    <table className={detailStyles.changes} aria-label={t("requests.history.details.changeTable", "Requested change, before and after")}>
      <thead>
        <tr>
          <td />
          {changes.map((change) => (
            <th key={change.vendorId} scope="col">
              <ProviderName vendorId={change.vendorId} name={providerName(change.vendorId)} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">{t("requests.history.details.before", "Before")}</th>
          {changes.map((change) => {
            const enabled = change.beforeEnabled === true;
            return (
              <td key={change.vendorId}>
                {enabled ? wholeDollars.format(change.beforeAmountUsd ?? 0) : (
                  <>
                    {notEnabled}
                    <span className="visually-hidden">{notEnabledForScreenReaders}</span>
                  </>
                )}
              </td>
            );
          })}
        </tr>
        <tr>
          <th scope="row">{t("requests.history.details.after", "After")}</th>
          {changes.map((change) => (
            <td key={change.vendorId} className={isChanged(change) ? detailStyles.changed : undefined}>
              {change.afterEnabled ? wholeDollars.format(change.afterAmountUsd) : <s>{wholeDollars.format(0)}</s>}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/**
 * The detail panel beneath an expanded request. It shows what the request actually asks for, so each request type gets its own content.
 * Who decided the request, its notes and its reference are left out: the
 * table already shows the first two.
 *
 * A manual vendor task's reason and instructions are written for the administrator who carries it out. The person it is
 * about sees only the tool and the action, and a line saying an administrator is doing it, or has done it, for them.
 */
export function RequestDetailContent({ requestType, payloadJson, providerChanges, forRequester = false, status }: RequestDetailContentProps): React.JSX.Element {
  const { t } = useTranslation();

  if (isProviderAllocationRequest(requestType)) {
    const changes = providerChanges ?? [];
    if (changes.length === 0) {
      return <p className={detailStyles.muted}>{t("requests.history.details.noChange", "No tool changes are recorded for this request.")}</p>;
    }

    return (
      <div className={detailStyles.details}>
        <h3 className={`panel-title ${detailStyles.heading}`}>{t("requests.history.details.change", "Requested change")}</h3>
        <div className={detailStyles.indented}>
          <ProviderChangeTable changes={changes} />
        </div>
      </div>
    );
  }

  const payload = parsePayload(payloadJson);
  if (payload === null) {
    return <p className={detailStyles.muted}>{t("requests.history.details.none", "No further details are recorded for this request.")}</p>;
  }

  if (requestType === ManualVendorAction && forRequester) {
    const task = manualVendorTask(payload);
    const explanation =
      status === "Pending"
        ? t("requests.history.details.manualPending", "An administrator is setting this up for you. There is nothing you need to do.")
        : status === "Completed"
          ? t("requests.history.details.manualCompleted", "An administrator has set this up for you.")
          : null;
    return (
      <div className={detailStyles.details}>
        {explanation && <p className={detailStyles.explanation}>{explanation}</p>}
        <dl className={detailStyles.facts}>
          <dt>{t("requests.history.details.vendor", "Tool")}</dt>
          <dd>
            <ProviderName vendorId={task.vendorId} name={t(`admin.entitlement.person.allocation.vendors.${task.vendorId}`, task.vendorId)} />
          </dd>
          <dt>{t("requests.history.details.action", "Action")}</dt>
          <dd>
            <ActionName action={task.action} />
          </dd>
        </dl>
      </div>
    );
  }

  if (requestType === ManualVendorAction) {
    const task = manualVendorTask(payload);
    return (
      <dl className={detailStyles.facts}>
        <dt>{t("requests.history.details.vendor", "Tool")}</dt>
        <dd>
            <ProviderName vendorId={task.vendorId} name={t(`admin.entitlement.person.allocation.vendors.${task.vendorId}`, task.vendorId)} />
          </dd>
        <dt>{t("requests.history.details.action", "Action")}</dt>
        <dd>
            <ActionName action={task.action} />
          </dd>
        <dt>{t("requests.history.details.reason", "Reason")}</dt>
        <dd>{task.reason}</dd>
        <dt>{t("requests.history.details.instructions", "Instructions")}</dt>
        <dd className={detailStyles.preWrap}>{task.instructions}</dd>
      </dl>
    );
  }

  if (requestType === JoinGroupRequest) {
    const reason = field(payload, "reason");
    return (
      <dl className={detailStyles.facts}>
        <dt>{t("requests.history.details.team", "Team")}</dt>
        <dd>
          <TeamName path={field(payload, "groupPath")} name={field(payload, "groupName")} />
        </dd>
        {reason !== "" && (
          <>
            <dt>{t("requests.history.details.message", "Message")}</dt>
            <dd className={detailStyles.preWrap}>{reason}</dd>
          </>
        )}
      </dl>
    );
  }

  // A request type with no bespoke layout yet: show its payload as recorded, so the details are never missing.
  return <pre className={detailStyles.raw}>{JSON.stringify(payload, null, 2)}</pre>;
}

/**
 * One person's requests as a table: what was asked, when, the outcome, who decided it and any notes. It shows the
 * rows it is given, so the caller decides what is in scope. With `expandable`, each row opens a detail panel.
 * Shared by the administrator's user page and the engineer's My Requests page.
 *
 * @param props.items - The requests to show, most recent first.
 * @param props.expandable - Whether each row can be expanded to show its details.
 */
/** The expanded row of the history table: the shared details for that row's request, then its timeline running across the row. */
function ExpandedRequestDetails({ data, forRequester, onCancelled }: ExpanderComponentProps<PersonRequestHistoryItem> & { forRequester?: boolean; onCancelled?: () => void }): React.JSX.Element {
  return (
    <div className={detailStyles.expanded}>
      <WaitingWith item={data} />
      <RequestDetailContent
        requestType={data.requestType}
        payloadJson={data.payloadJson}
        providerChanges={data.providerChanges}
        forRequester={forRequester}
        status={data.status}
      />
      <div className={detailStyles.timeline}>
        <RequestTimeline item={data} orientation="horizontal" />
      </div>
      {onCancelled && <CancelRequestButton requestId={data.id} status={data.status} onCancelled={onCancelled} />}
    </div>
  );
}

/**
 * Gives the expander column its roles. The library leaves the expander header cell and the expander cell in each row as
 * plain `div`s, so a row holds a child with no role and assistive technology skips the expander button's structure.
 * The library only adds roles when its keyboard navigation is on, and that would change how the whole table behaves.
 * The roles are set on the DOM and re-applied whenever the library re-renders its rows, since sorting and paging
 * happen inside the library and do not re-render this component.
 *
 * @param ref - The wrapper element that contains the table.
 * The expander header cell has no text, so it gets a screen-reader-only label, or it is an unnamed table header.
 *
 * @param ref - The wrapper element that contains the table.
 * @param enabled - Whether the table has an expander column at all.
 * @param headerLabel - The accessible name of the expander header cell.
 */
function useExpanderColumnRoles(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean, headerLabel: string): void {
  useEffect(() => {
    const wrapper = ref.current;
    if (!enabled || !wrapper) {
      return undefined;
    }

    const applyRoles = (): void => {
      wrapper.querySelectorAll<HTMLElement>(".rdt_columnExpander").forEach((cell) => {
        cell.setAttribute("role", "columnheader");
        if (!cell.querySelector(".visually-hidden")) {
          const label = document.createElement("span");
          label.className = "visually-hidden";
          label.textContent = headerLabel;
          cell.append(label);
        }
      });
      wrapper.querySelectorAll<HTMLElement>(".rdt_cellExpander").forEach((cell) => {
        cell.setAttribute("role", "cell");
      });
    };

    applyRoles();
    const observer = new MutationObserver(applyRoles);
    observer.observe(wrapper, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [ref, enabled, headerLabel]);
}

export function RequestHistoryTable({
  items,
  expandable = false,
  forRequester = false,
  loadingLabel,
  emptyContent,
  onCancelled,
}: {
  items: PersonRequestHistoryItem[];
  expandable?: boolean;
  /** Names and explains each request for the person it is about rather than for an administrator. */
  forRequester?: boolean;
  /** While the requests load: the table keeps its header and shows the spinner, with this label, in its body. */
  loadingLabel?: string;
  /** When there are no rows to show, what the body holds instead (a failed load's error, say), under the header. */
  emptyContent?: React.ReactNode;
  /** When given, a pending request can be cancelled from its expanded row, and this is called once it has been. */
  onCancelled?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useExpanderColumnRoles(wrapperRef, expandable, t("requests.history.columns.details", "Details"));

  const columns = useMemo<TableColumn<PersonRequestHistoryItem>[]>(
    () => [
      { id: "type", name: t("requests.history.columns.request", "Request"), sortable: true, selector: (row) => typeLabel(row.requestType, forRequester) },
      { id: "created", name: t("requests.history.columns.logged", "Logged"), sortable: true, selector: (row) => row.createdAt, format: (row) => formatDate(row.createdAt) },
      { id: "outcome", name: t("requests.history.columns.outcome", "Outcome"), sortable: true, selector: (row) => statusLabel(row.status) },
      { id: "decidedBy", name: t("requests.history.columns.decidedBy", "Decided by"), sortable: true, selector: (row) => row.decidedByName ?? shortEmail(row.decidedByEmail) ?? "—" },
      { id: "decidedAt", name: t("requests.history.columns.decided", "Decided"), sortable: true, selector: (row) => row.decidedAt ?? "", format: (row) => formatDate(row.decidedAt) },
      { id: "notes", name: t("requests.history.columns.notes", "Notes"), selector: (row) => row.decisionNotes ?? "—", wrap: true },
    ],
    [t, forRequester],
  );

  return (
    <div ref={wrapperRef} className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`}>
      <DataTable
        columns={columns}
        data={items}
        className={styles.peopleDataTable}
        keyField="id"
        ariaLabel={t("requests.history.tableLabel", "Requests")}
        theme={adminTableTheme}
        fixedHeader
        fixedHeaderScrollHeight="100%"
        striped
        highlightOnHover
        columnSeparator="subtle"
        pagination
        paginationComponent={TablePager}
        paginationPerPage={25}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        defaultSortFieldId="created"
        defaultSortAsc={false}
        expandableRows={expandable}
        expandableRowsComponent={expandable ? ExpandedRequestDetails : undefined}
        expandableRowsComponentProps={{ forRequester, onCancelled }}
        progressPending={loadingLabel !== undefined}
        progressSkeleton={false}
        progressComponent={
          <div className={detailStyles.loading}>
            <LoadingState label={loadingLabel} />
          </div>
        }
        persistTableHead
        noDataComponent={emptyContent === undefined ? undefined : <div className={detailStyles.empty}>{emptyContent}</div>}
      />
    </div>
  );
}
